/**
 * Vocabulaire produit-aware — centralise les mots qui changent selon le produit.
 *
 * Problème : `packages/core` est partagé entre les 4 apps. Une string en dur
 * comme "Voter" est correcte pour date-polls mais trompeuse pour form-polls
 * (devrait dire "Répondre"). Ce mapping permet à chaque composant core de
 * choisir le bon mot via `vocab(productType).xxx` sans casser les autres apps.
 *
 * Sprint 2 form-polls launch (rebrand core, 31/05/2026). Bug test : "Votes par
 * participant", "Participer au vote", "votre sondage", "Créé avec DooDates"
 * affichés tels quels sur form-polls.
 *
 * Usage :
 *   import { vocab } from "@core/lib/productVocabulary";
 *   const v = vocab(productType);
 *   <button>{v.respondAction}</button>   // "Répondre" (form) | "Voter" (date)
 */

import type { ProductType } from "@core/config/products.config";

export interface ProductVocab {
  /** Nom de marque. DooQuest (form) | DooDates (date/autres). */
  brand: string;
  /** Le sondage/formulaire lui-même. "formulaire" | "sondage". */
  item: string;
  /** Idem capitalisé pour début de phrase. "Formulaire" | "Sondage". */
  itemCap: string;
  /** Action de l'utilisateur final. "Répondre" | "Voter". */
  respondAction: string;
  /** CTA participer. "Répondre au formulaire" | "Participer au vote". */
  respondCta: string;
  /** Une soumission. "réponse" | "vote". */
  submission: string;
  /** Idem pluriel. "réponses" | "votes". */
  submissions: string;
  /** Celui qui répond. "participant" | "participant" (identique, gardé pour cohérence). */
  respondent: string;
  /** Erreur 404 ressource introuvable. "Formulaire introuvable" | "Sondage introuvable". */
  notFound: string;
  /** Verbe infinitif minuscule. "répondre" | "voter". */
  respondVerb: string;
  /** Fragment alerte nouvelle soumission (accord du genre). "à chaque nouvelle réponse" | "à chaque nouveau vote". */
  notifyNewSubmission: string;
}

const FORM_VOCAB: ProductVocab = {
  brand: "DooQuest",
  item: "formulaire",
  itemCap: "Formulaire",
  respondAction: "Répondre",
  respondCta: "Répondre au formulaire",
  submission: "réponse",
  submissions: "réponses",
  respondent: "participant",
  notFound: "Formulaire introuvable",
  respondVerb: "répondre",
  notifyNewSubmission: "à chaque nouvelle réponse",
};

const DEFAULT_VOCAB: ProductVocab = {
  brand: "DooDates",
  item: "sondage",
  itemCap: "Sondage",
  respondAction: "Voter",
  respondCta: "Participer au vote",
  submission: "vote",
  submissions: "votes",
  respondent: "participant",
  notFound: "Sondage introuvable",
  respondVerb: "voter",
  notifyNewSubmission: "à chaque nouveau vote",
};

/**
 * Retourne le vocabulaire adapté au produit. Tout sauf "form" → vocab par
 * défaut (date/availability/quizz gardent "sondage/voter" — comportement
 * actuel inchangé, donc 0 régression pour date-polls LIVE).
 */
export function vocab(productType?: ProductType | string | null): ProductVocab {
  return productType === "form" ? FORM_VOCAB : DEFAULT_VOCAB;
}

/**
 * Vocabulaire déduit du host pour les composants partagés sans contexte produit
 * (auth modals, AuthRequired). form-polls tourne sur un host contenant "form"
 * (form-polls.doodates.com, previews form-polls-*.vercel.app) → DooQuest. Tout
 * le reste (doodates.com, localhost date-polls) → DooDates. SSR/test-safe.
 *
 * Limite connue : en dev local (localhost:port) le host ne contient pas "form"
 * → fallback DooDates. Acceptable, le brand correct s'affiche en preview/prod.
 */
export function vocabFromHost(): ProductVocab {
  if (typeof window !== "undefined" && /form/i.test(window.location.hostname)) {
    return FORM_VOCAB;
  }
  return DEFAULT_VOCAB;
}
